import { useHealth, useListIntegrations, useMe, AuthRole } from '@smarthome/api';
import { ROUTES } from '@smarthome/ui';
import { ChevronRight, RefreshCw } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { Tooltip } from '@/components/ui/overlays';

// Map integration IDs to their settings routes
const integrationSettingsRoutes: Record<string, string> = {
  eos: ROUTES.EOS_SETTINGS,
  'ikea-dirigera': ROUTES.DIRIGERA_SETTINGS,
  http: ROUTES.HTTP_SETTINGS,
  matter: ROUTES.MATTER_SETTINGS,
  mqtt: ROUTES.MQTT_SETTINGS,
  'philips-hue': ROUTES.HUE_SETTINGS,
  shelly: ROUTES.SHELLY_SETTINGS,
  slide: ROUTES.SLIDE_SETTINGS,
  webos: ROUTES.WEBOS_SETTINGS,
};

/**
 * Settings page - clean grouped list design
 */
export function SettingsPage() {
  const { data: integrationsData, isLoading: integrationsLoading } = useListIntegrations();
  const { data: healthData } = useHealth();
  const { data: meData } = useMe();
  const isAdmin = meData?.user?.role === AuthRole.RoleAdmin;
  const [isDarkMode, setIsDarkMode] = useState(
    () => window.matchMedia('(prefers-color-scheme: dark)').matches
  );

  const handleReload = () => {
    window.location.reload();
  };

  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (e: MediaQueryListEvent) => setIsDarkMode(e.matches);
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  // Group settings into logical categories
  const settingsGroups = [
    {
      title: 'System & Monitoring',
      items: [
        { name: 'Server Info', description: 'Health, uptime and status', href: ROUTES.SERVER_INFO },
        { name: 'Logs', description: 'View system logs', href: ROUTES.LOGS },
        {
          name: 'Heartbeats',
          description: 'Outgoing pings for external monitoring',
          href: ROUTES.SETTINGS_HEARTBEATS,
        },
      ],
    },
    {
      title: 'Account & Security',
      items: [
        { name: 'API Keys', description: 'Manage access tokens', href: ROUTES.API_KEYS },
        { name: 'Users', description: 'User accounts and permissions' },
      ],
    },
    {
      title: 'Personalization',
      items: [
        {
          name: 'Home Location',
          description: 'Used for sunrise/sunset and day/night scenes',
          href: ROUTES.SETTINGS_HOME_LOCATION,
        },
        {
          name: 'Regional',
          description: 'Currency and regional defaults',
          href: ROUTES.SETTINGS_REGIONAL,
        },
        { name: 'Unit Types', description: 'Manage measurement units', href: ROUTES.UNIT_TYPES },
      ],
    },
    {
      title: 'Automation',
      items: [
        {
          name: 'Inputs',
          description: 'Map buttons and dials to scenes or device actions',
          href: ROUTES.SETTINGS_INPUTS,
        },
      ],
    },
    {
      title: 'Communication',
      items: [
        {
          name: 'Notifications',
          description: 'Choose email, SMS, and push alert delivery',
          href: ROUTES.SETTINGS_NOTIFICATIONS,
        },
        ...(isAdmin
          ? [
              {
                name: 'Channels',
                description: 'Email and SMS providers for notifications',
                href: ROUTES.SETTINGS_CHANNELS,
              },
            ]
          : []),
      ],
    },
  ];

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-semibold tracking-tight text-gray-900 dark:text-white">
          Settings
        </h1>
        <p className="mt-1 text-[15px] text-gray-500 dark:text-zinc-500">
          Manage integrations and system configuration
        </p>
      </div>

      {/* Settings Groups */}
      {settingsGroups.map((group) => (
        <div key={group.title}>
          <h2 className="mb-4 text-sm font-medium uppercase tracking-wider text-gray-500 dark:text-zinc-500">
            {group.title}
          </h2>
          <div className="overflow-hidden rounded-xl border border-gray-200 bg-white dark:border-white/[0.06] dark:bg-[#18181b]">
            {group.items.map((item, index) => {
              const className = `flex w-full items-center justify-between gap-4 p-4 text-left transition-all hover:bg-gray-50 dark:hover:bg-white/[0.03] ${
                index !== 0 ? 'border-t border-gray-200 dark:border-white/[0.06]' : ''
              }`;

              const content = (
                <>
                  <div className="min-w-0 flex-1">
                    <div className="text-[15px] font-medium text-gray-900 dark:text-white">
                      {item.name}
                    </div>
                    <div className="mt-0.5 text-sm text-gray-500 dark:text-zinc-500">
                      {item.description}
                    </div>
                  </div>
                  <ChevronRight className="h-5 w-5 flex-shrink-0 text-gray-400 dark:text-zinc-600" />
                </>
              );

              if ('href' in item && item.href) {
                return (
                  <Link key={item.name} to={item.href} className={className}>
                    {content}
                  </Link>
                );
              }

              return (
                <button key={item.name} className={className}>
                  {content}
                </button>
              );
            })}
          </div>
        </div>
      ))}

      {/* Integrations Section */}
      <div>
        <h2 className="mb-4 text-sm font-medium uppercase tracking-wider text-gray-500 dark:text-zinc-500">
          Integrations
        </h2>
        <div className="overflow-hidden rounded-xl border border-gray-200 bg-white dark:border-white/[0.06] dark:bg-[#18181b]">
          {integrationsLoading ? (
            <div className="flex items-center justify-center p-8">
              <div className="h-5 w-5 animate-spin rounded-full border-2 border-gray-300 border-t-blue-500 dark:border-zinc-700" />
            </div>
          ) : integrationsData?.integrations && integrationsData.integrations.length > 0 ? (
            [...integrationsData.integrations]
              .sort((a, b) => (a.displayName ?? '').localeCompare(b.displayName ?? ''))
              .map((integration, index) => {
                const settingsRoute = integration.id
                  ? integrationSettingsRoutes[integration.id]
                  : undefined;
                const className = `flex w-full items-center justify-between gap-4 p-4 text-left transition-all hover:bg-gray-50 dark:hover:bg-white/[0.03] ${
                  index !== 0 ? 'border-t border-gray-200 dark:border-white/[0.06]' : ''
                }`;

                const content = (
                  <>
                    <div className="flex min-w-0 flex-1 items-center gap-4">
                      <div className="relative flex h-10 w-10 flex-shrink-0 items-center justify-center">
                        {(isDarkMode ? integration.iconDark : integration.iconLight) ? (
                          <div
                            className="h-8 w-8 [&>svg]:h-full [&>svg]:w-full"
                            dangerouslySetInnerHTML={{
                              __html:
                                (isDarkMode ? integration.iconDark : integration.iconLight) ?? '',
                            }}
                          />
                        ) : (
                          <div className="h-8 w-8 rounded-lg bg-gray-100 dark:bg-zinc-800" />
                        )}
                        <div
                          className={`absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 rounded-full border-2 border-white dark:border-[#18181b] ${
                            integration.health?.healthy
                              ? 'bg-emerald-400'
                              : 'bg-gray-300 dark:bg-zinc-600'
                          }`}
                        />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-[15px] font-medium text-gray-900 dark:text-white">
                          {integration.displayName}
                        </div>
                        <div className="mt-0.5 truncate text-sm text-gray-500 dark:text-zinc-500">
                          {integration.description}
                        </div>
                      </div>
                    </div>
                    <ChevronRight className="h-5 w-5 flex-shrink-0 text-gray-400 dark:text-zinc-600" />
                  </>
                );

                if (settingsRoute) {
                  return (
                    <Link key={integration.id} to={settingsRoute} className={className}>
                      {content}
                    </Link>
                  );
                }

                return (
                  <button key={integration.id} className={className}>
                    {content}
                  </button>
                );
              })
          ) : (
            <div className="p-4 text-center text-sm text-gray-500 dark:text-zinc-500">
              No integrations configured
            </div>
          )}
        </div>
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between border-t border-gray-200 pt-4 dark:border-white/[0.06]">
        <div className="flex items-center gap-3 text-sm text-gray-500 dark:text-zinc-600">
          <span>Smarthome</span>
          <div className="flex items-center gap-2">
            {healthData?.version && (
              <Tooltip
                content={
                  <div className="flex flex-col gap-1">
                    <div>Tag: {healthData.version}</div>
                    <div>
                      Built:{' '}
                      {healthData?.buildTime
                        ? new Date(healthData.buildTime).toLocaleString()
                        : 'N/A'}
                    </div>
                  </div>
                }
                position="top"
                className="max-w-none whitespace-nowrap"
              >
                <span className="cursor-default rounded bg-gray-100 px-1.5 py-0.5 font-mono text-xs dark:bg-zinc-800">
                  BE: {healthData.version.slice(0, 7)}
                </span>
              </Tooltip>
            )}
            {__BUILD_VERSION__ && (
              <Tooltip
                content={
                  <div className="flex flex-col gap-1">
                    <div>Tag: {__BUILD_VERSION__}</div>
                    <div>
                      Built: {__BUILD_TIME__ ? new Date(__BUILD_TIME__).toLocaleString() : 'N/A'}
                    </div>
                  </div>
                }
                position="top"
                className="max-w-none whitespace-nowrap"
              >
                <span className="cursor-default rounded bg-gray-100 px-1.5 py-0.5 font-mono text-xs dark:bg-zinc-800">
                  FE: {__BUILD_VERSION__.slice(0, 7)}
                </span>
              </Tooltip>
            )}
          </div>
        </div>
        <button
          onClick={handleReload}
          className="flex items-center gap-1.5 text-sm text-gray-400 transition-colors hover:text-gray-600 dark:text-zinc-600 dark:hover:text-zinc-400"
        >
          <RefreshCw className="h-3.5 w-3.5" />
          Reload
        </button>
      </div>
    </div>
  );
}
